import {
  Avatar,
  Button,
  Dialog,
  DialogContent,
  DialogActions,
  Menu,
  Typography,
  DialogTitle,
  MenuItem,
} from "@mui/material";
import cx from "classnames";
import React, {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";

import { ReactComponent as CrownIcon } from "assets/crown.svg";
import { ReactComponent as DropdownIcon } from "assets/dropdown-icon.svg";
import { ReactComponent as EyeIcon } from "assets/eye.svg";
import { ReactComponent as HelpIcon } from "assets/help.svg";
import { ReactComponent as LogoutIcon } from "assets/logout-icon.svg";
import BannerBar from "components/Banner";
import Notifications from "components/Notifications";
import ProjectSearchBar from "components/ProjectSearchBar";
import SecondaryVerification from "components/SecondaryVerification";
import { AUTH_USER, PLANS, WINDOW_EVENTS } from "constants/constants";
import { Preferences, User } from "models/User";
import DownloadsButton from "pages/Downloads";

import styles from "./SlapshotMainLayout.module.scss";
import Logo from "../../assets/logo.png";
import UploadAnnotations from "../../components/UploadAnnotations";
import { SLAPSHOT_SUPPORT_URL } from "../../constants/env";
import {
  ADMIN_URL,
  SLAPSHOT_HOME_URL,
  SLAPSHOT_LOGIN_URL,
  SLAPSHOT_PROFILE_URL,
} from "../../constants/urls";
import {
  getUserPreferences,
  getUserProfilePicture,
  updateUserPreferences,
  refreshUser,
  exitImpersonation,
  updateUserAccessInfo,
} from "../../services/UserService";
import { getFirstName, getInitials, getUser } from "../../utils/helper";

interface UserContextType {
  preferences?: Preferences;
  setPreferences: (
    key: keyof Preferences,
    value: unknown,
    append: boolean,
  ) => void;
  samWorker: Worker | null;
}

export const UserContext = createContext<UserContextType | null>(null);

const SlapshotMainLayout = ({ samWorker }: { samWorker: Worker | null }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const user = getUser();
  const [userProfilePicture, setUserProfilePicture] = useState<string | null>(
    null,
  );
  const [lastActiveAt, setLastActiveAt] = useState(new Date());
  const [showInferenceNotification, setShowInferenceNotification] =
    useState(false);
  const [showInferencedShotsNotification, setShowInferencedShotsNotification] =
    useState(false);
  const [showUploadAnnotation, setShowUploadAnnotation] = useState(false);
  const [unInferencedShots, setUnInferencedShots] = useState<{
    [key: string]: string;
  }>({});

  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);

  const [shots, setShots] = useState<string[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [preferences, setPreferences] = useState<Preferences | undefined>(
    undefined,
  );
  const [impersonatingUser, setImpersonatingUser] = useState<User | null>(null);
  const [planTag, setPlanTag] = useState<string | null>(null);
  const [requiresVerification, setRequiresVerification] = useState(false);

  const userContextValue = useMemo(() => {
    return {
      preferences,
      setPreferences: (key, value, append) => {
        if (append) {
          const oldValue = [...(preferences?.[key] || [])];
          setPreferences({
            ...preferences,
            [key]: [...oldValue, value],
          });
        } else {
          setPreferences({
            ...preferences,
            [key]: value,
          });
        }

        updateUserPreferences(key, value, append)
          .then((data) => {
            const settings: Preferences =
              typeof data?.preferences === "string"
                ? JSON.parse(data?.preferences)
                : data?.preferences || {};
            setPreferences(settings);
          })
          .catch((e) => console.error(e));
      },
      samWorker,
    };
  }, [preferences, samWorker]);

  useEffect(() => {
    const handleShowPlanTag = (event: CustomEvent) => {
      const plan = event.detail?.plan;
      if (plan) {
        setPlanTag(plan);
      }
    };

    const handleHidePlanTag = () => {
      setPlanTag(null);
    };

    window.addEventListener(
      WINDOW_EVENTS.SHOW_PLAN_TAG,
      handleShowPlanTag as EventListener,
    );
    window.addEventListener(WINDOW_EVENTS.HIDE_PLAN_TAG, handleHidePlanTag);
    document.title = "Slapshot";

    return () => {
      window.removeEventListener(
        WINDOW_EVENTS.SHOW_PLAN_TAG,
        handleShowPlanTag as EventListener,
      );
      window.removeEventListener(
        WINDOW_EVENTS.HIDE_PLAN_TAG,
        handleHidePlanTag,
      );
    };
  }, []);

  const refreshUserInfo = useCallback(async () => {
    await refreshUser().then((data) => {
      localStorage.setItem(AUTH_USER, JSON.stringify(data.user));
      setRequiresVerification(!!data.user?.requires_secondary_verification);
      if (data.impersonating_user) {
        setImpersonatingUser(data.impersonating_user);
      }
      if (!data.user) {
        window.location.pathname = SLAPSHOT_LOGIN_URL;
      }
    });
    // Routine refresh — Fingerprint is only spent at registration and login.
    await updateUserAccessInfo().catch((e) => {
      console.log(e);
    });
  }, []);

  useEffect(() => {
    const updateActivity = () => {
      if (document.visibilityState === "visible") {
        const now = new Date();
        if (now.toDateString() !== lastActiveAt.toDateString()) {
          refreshUserInfo();
        }
        setLastActiveAt(now);
      }
    };
    document.addEventListener("visibilitychange", updateActivity);
    return () => {
      document.removeEventListener("visibilitychange", updateActivity);
    };
  }, [lastActiveAt, refreshUserInfo]);

  useEffect(() => {
    refreshUserInfo();

    getUserPreferences()
      .then((data) => {
        const settings: Preferences =
          typeof data?.preferences === "string"
            ? JSON.parse(data?.preferences)
            : data?.preferences || {};
        setPreferences(settings);
      })
      .catch((e) => console.error(e));

    getUserProfilePicture().then((res) => {
      setUserProfilePicture(res.profile_picture_url);
    });
  }, [refreshUserInfo]);

  return (
    <UserContext.Provider value={userContextValue}>
      <div className={cx("slapshotBackground", styles.mainContainer)}>
        <BannerBar />
        <div className={styles.header}>
          <div className={styles.logoContainer}>
            <img
              src={Logo}
              alt="Slapshot"
              className={styles.slapshotLogo}
              onClick={() => {
                navigate(SLAPSHOT_HOME_URL);
              }}
            />

            <div className={styles.planTagContainer}>
              {planTag === PLANS.FREE && (
                <div className={styles.freePlan}>FREE PLAN</div>
              )}

              {planTag && planTag !== PLANS.FREE && (
                <div className={styles.paidPlan}>
                  <CrownIcon className={styles.crownIcon} />
                  {planTag}
                </div>
              )}
            </div>
          </div>

          <div className={styles.navButtons}>
            <Button
              className={cx(
                styles.navButton,
                location.pathname === SLAPSHOT_HOME_URL &&
                  styles.navButtonActive,
              )}
              onClick={() => {
                navigate(SLAPSHOT_HOME_URL);
              }}
            >
              Workspaces
            </Button>
            <DownloadsButton />
          </div>

          <div className={styles.headerUtils}>
            <ProjectSearchBar />
            <Notifications />
            <a
              className={styles.helpButton}
              target="_blank"
              rel="noreferrer"
              href={SLAPSHOT_SUPPORT_URL}
            >
              <HelpIcon />
              <span>Help</span>
            </a>
            <div
              className={styles.userDetails}
              onClick={(e) => {
                setAnchorEl(e.currentTarget);
              }}
            >
              {userProfilePicture ? (
                <img
                  className={styles.profilePicture}
                  src={userProfilePicture}
                  alt={userProfilePicture}
                />
              ) : (
                <Avatar
                  className={styles.avatarButton}
                  {...{ children: getInitials() }}
                  sx={{ bgcolor: "red", width: 14, height: 14 }}
                />
              )}
              <p>Hi {getFirstName()}</p>
              <button type="button" className={styles.dropdownButton}>
                <DropdownIcon />
              </button>
            </div>
          </div>
        </div>
        {impersonatingUser && (
          <div className={styles.impersonationIndicator}>
            You are impersonating {user.email}, to exit impersonation click
            <button
              type="button"
              onClick={() => {
                exitImpersonation().then(() => {
                  setImpersonatingUser(null);
                  localStorage.clear();
                  // redirection to admin won't work in local.
                  navigate(ADMIN_URL);
                });
              }}
            >
              here
            </button>
          </div>
        )}
        <div className={styles.contentContainer}>
          <Outlet />
        </div>
        <SecondaryVerification
          open={requiresVerification}
          onVerified={() => {
            setRequiresVerification(false);
          }}
        />
        <UploadAnnotations
          open={showUploadAnnotation}
          onClose={() => {
            setShowUploadAnnotation(false);
          }}
        />
        <Menu
          id="basic-menu"
          anchorEl={anchorEl}
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "right",
          }}
          transformOrigin={{ vertical: "top", horizontal: "right" }}
          open={!!anchorEl}
          onClose={() => {
            setAnchorEl(null);
          }}
          sx={{
            "& .MuiPaper-root": {
              backgroundColor: "#1E1F23",
              borderRadius: "20px",
              border: "1px solid #334155",
              width: "260px",
              boxShadow: "0px 8px 10px -6px #0000001A",
              mt: 2.5,
            },
          }}
        >
          <div className={styles.menuDetails}>
            <h3>{getFirstName()}</h3>
            {/* TODO: fetch role from db */}
            <p>Admin</p>
          </div>
          <div className={styles.menuItems}>
            <MenuItem
              className={styles.menuButton}
              id="LayerRenameBtn"
              onClick={() => {
                navigate(SLAPSHOT_PROFILE_URL);
                setAnchorEl(null);
              }}
            >
              <EyeIcon />
              Profile Settings
            </MenuItem>
            <MenuItem
              className={cx(styles.menuButton, styles.logoutButton)}
              onClick={() => {
                localStorage.clear();
                navigate(SLAPSHOT_LOGIN_URL);
              }}
            >
              <LogoutIcon />
              Log out
            </MenuItem>
          </div>
        </Menu>
        <Dialog open={showInferenceNotification} fullWidth maxWidth="xs">
          <DialogContent>
            <Typography>Your inference is now running.</Typography>
            <Typography>
              We'll send you an email as soon as it is done!
            </Typography>
          </DialogContent>

          <DialogActions>
            <Button
              onClick={() => {
                setShowInferenceNotification(false);
              }}
            >
              Close
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog open={!!error} fullWidth maxWidth="xs">
          <DialogTitle align="center">Error...</DialogTitle>
          <DialogContent>{error}</DialogContent>
          <DialogActions>
            <Button
              onClick={() => {
                setError(null);
              }}
            >
              Close
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog open={showInferencedShotsNotification} maxWidth="sm">
          <DialogContent>
            <div className={styles.successDialog}>
              <b>Inference Submitted Successfully</b>
            </div>
            {shots && (
              <Typography>Started inference for following shots:</Typography>
            )}
            {shots && (
              <ul>
                {shots.map((shot, index) => (
                  <li key={index}>
                    <b>{shot}</b>
                  </li>
                ))}
              </ul>
            )}
            {Object.keys(unInferencedShots).length > 0 && (
              <Typography>Inference for following shots Failed.</Typography>
            )}
            <ul>
              {Object.entries(unInferencedShots).map(([key, value]) => (
                <li key={key}>
                  <p>
                    <b>{key}</b> : {value}
                  </p>
                </li>
              ))}
            </ul>
            {Object.keys(unInferencedShots).length > 0 && (
              <Typography className={styles.reInferenceMessage}>
                Please try to inference for above shots again in some time.
              </Typography>
            )}
            <Typography>
              <b>We'll send you an email soon!</b>
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => {
                setShowInferencedShotsNotification(false);
              }}
            >
              Close
            </Button>
          </DialogActions>
        </Dialog>
      </div>
    </UserContext.Provider>
  );
};

export default SlapshotMainLayout;
